import { BillingPage } from '@/features/billing/BillingPage';
import { AdminPage } from '@/features/admin/AdminPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { GoogleSetupPage } from '@/features/integrations/GoogleSetupPage';
/** Route table and the authenticated/anonymous split. */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '@/app/auth';
import { AppLayout } from '@/layouts/AppLayout';
import {
  LoadingState,
} from '@/components/ui';

import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { SitesPage } from '@/features/sites/SitesPage';
import { NewSitePage } from '@/features/sites/NewSitePage';
import { SiteSettingsPage } from '@/features/sites/SiteSettingsPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { IssuesPage } from '@/features/issues/IssuesPage';
import { IssueDetailPage } from '@/features/issues/IssueDetailPage';
import { OpportunitiesPage } from '@/features/opportunities/OpportunitiesPage';
import { AiAnalysisPage } from '@/features/analysis/AiAnalysisPage';
import { KeywordResearchPage } from '@/features/research/KeywordResearchPage';
import { SeoRoadmapPage } from '@/features/research/SeoRoadmapPage';
import { PagesPage } from '@/features/pages/PagesPage';
import { ActionQueuePage } from '@/features/actions/ActionQueuePage';
import { ActionDetailPage } from '@/features/actions/ActionDetailPage';
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { LandingPage } from '@/features/marketing/LandingPage';
import { NotFoundPage } from '@/features/shared/NotFoundPage';

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState label="Restoring your session" />;
  if (status === 'anonymous') {
    // Preserve where they were headed so login can return them there.
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

/**
 * Keeps a signed-in user off the sign-in and sign-up screens.
 *
 * ``to`` matters: registering flips auth status, which re-renders this guard
 * and navigates immediately — racing the page's own post-signup navigation.
 * Giving the guard the correct destination removes the race rather than
 * trying to win it.
 */
function RedirectIfAuthenticated({
  children,
  to = '/sites',
}: {
  children: ReactNode;
  to?: string;
}) {
  const { status } = useAuth();
  if (status === 'loading') return <LoadingState />;
  if (status === 'authenticated') return <Navigate to={to} replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* The public front door. A signed-in visitor goes straight to their
          sites rather than being marketed to. */}
      <Route
        path="/"
        element={
          <RedirectIfAuthenticated to="/sites">
            <LandingPage />
          </RedirectIfAuthenticated>
        }
      />

      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/register"
        element={
          // A brand-new account has no websites yet, so send them straight to
          // the onboarding form.
          <RedirectIfAuthenticated to="/billing">
            <RegisterPage />
          </RedirectIfAuthenticated>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/sites" element={<SitesPage />} />
        <Route path="/sites/new" element={<NewSitePage />} />
        <Route path="/sites/:siteId" element={<DashboardPage />} />
        <Route path="/sites/:siteId/reports" element={<ReportsPage />} />
        <Route path="/sites/:siteId/search-setup" element={<GoogleSetupPage />} />
        <Route path="/sites/:siteId/issues" element={<IssuesPage />} />
        <Route path="/sites/:siteId/issues/:issueId" element={<IssueDetailPage />} />
        <Route path="/sites/:siteId/opportunities" element={<OpportunitiesPage />} />
        <Route path="/sites/:siteId/analysis" element={<AiAnalysisPage />} />
        <Route path="/sites/:siteId/keywords" element={<KeywordResearchPage />} />
        <Route path="/sites/:siteId/roadmap" element={<SeoRoadmapPage />} />
        <Route path="/sites/:siteId/pages" element={<PagesPage />} />
        <Route path="/sites/:siteId/actions" element={<ActionQueuePage />} />
        <Route path="/sites/:siteId/settings" element={<SiteSettingsPage />} />
        <Route path="/actions" element={<ActionQueuePage />} />
        <Route path="/actions/:actionId" element={<ActionDetailPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
